// Supplier contracts register + create/edit (BUILD_SPEC.md §5.4, §7). Plan.md
// G20 is the SOLE web owner of this screen (QC fix #1) — no other module goal may
// add a contracts UI. FLAT_WET/BDT take a flat price; MOISTURE_BAND takes a list
// of non-overlapping, contiguous bands — the same cross-field rule
// packages/core/src/schemas/purchasing.ts's `checkPricingBasis` enforces
// server-side (a malformed band list is rejected there with a 400, not silently
// accepted), so this editor just needs to make the common case easy to enter.
import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconFileOff, IconPlus, IconTrash } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { PRICING_BASES, type PricingBasis } from '@zebra/core';
import { formatSastDate, sastDayEnd, sastDayStart, todayIsoDate } from '../../lib/sastDate';
import { centsToRand, PRICING_BASIS_LABELS, randToCents } from './common';
import {
  type SupplierContractRow,
  type SupplierContractWriteInput,
  useCreateSupplierContract,
  useProducts,
  useSupplierContracts,
  useSuppliers,
  useUpdateSupplierContract,
} from './api';

interface BandFormValue {
  mcMin: number;
  mcMax: number;
  priceRand: string;
}

interface ContractFormValues {
  supplierId: string;
  productId: string;
  pricingBasis: PricingBasis;
  priceRand: string;
  bands: BandFormValue[];
  validFrom: string;
  validTo: string;
  isActive: boolean;
}

function toFormValues(row?: SupplierContractRow): ContractFormValues {
  return {
    supplierId: row?.supplierId ?? '',
    productId: row?.productId ?? '',
    pricingBasis: row?.pricingBasis ?? 'FLAT_WET',
    priceRand: centsToRand(row?.priceCentsPerTonne ?? null),
    bands: row?.bands?.map((b) => ({ mcMin: b.mcMin, mcMax: b.mcMax, priceRand: centsToRand(b.priceCents) })) ?? [],
    // SAST calendar dates, not `.slice(0, 10)` UTC truncation (spec §4 item 7,
    // Amendment 2026-07-10): a stored SAST day-start instant is 22:00Z on the
    // PREVIOUS UTC day, so UTC truncation would prefill the edit form one day early.
    validFrom: row?.validFrom ? formatSastDate(row.validFrom) : todayIsoDate(),
    validTo: row?.validTo ? formatSastDate(row.validTo) : '',
    isActive: row?.isActive ?? true,
  };
}

function toInput(values: ContractFormValues): SupplierContractWriteInput {
  const isBand = values.pricingBasis === 'MOISTURE_BAND';
  return {
    supplierId: values.supplierId,
    productId: values.productId,
    pricingBasis: values.pricingBasis,
    priceCentsPerTonne: isBand ? null : randToCents(values.priceRand),
    bands: isBand ? values.bands.map((b) => ({ mcMin: b.mcMin, mcMax: b.mcMax, priceCents: randToCents(b.priceRand) })) : null,
    // The validity window is inclusive of both whole SAST business days (spec §4
    // item 7, Amendment 2026-07-10). A bare `new Date('YYYY-MM-DD')` is UTC midnight
    // = 02:00 SAST: the contract would not yet apply for the first two SAST hours of
    // its first day, and `validTo` would cut out at 02:00 SAST on its stated last
    // day — losing most of a day the user intended to cover.
    validFrom: sastDayStart(values.validFrom),
    validTo: values.validTo ? sastDayEnd(values.validTo) : null,
    isActive: values.isActive,
  };
}

function ContractFormModal({
  opened,
  onClose,
  editing,
}: {
  opened: boolean;
  onClose: () => void;
  editing: SupplierContractRow | null;
}) {
  const { data: suppliers } = useSuppliers({ isActive: true });
  const { data: products } = useProducts({ isActive: true });
  const canOperate = useCanOperate();
  const create = useCreateSupplierContract();
  const update = useUpdateSupplierContract(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<ContractFormValues>({
    initialValues: toFormValues(editing ?? undefined),
    validate: {
      supplierId: (v) => (v ? null : 'Supplier is required'),
      productId: (v) => (v ? null : 'Product is required'),
      priceRand: (v, values) => (values.pricingBasis !== 'MOISTURE_BAND' && !v ? 'Price is required' : null),
      bands: (v, values) => (values.pricingBasis === 'MOISTURE_BAND' && v.length === 0 ? 'At least one band is required' : null),
    },
  });

  function handleSubmit(values: ContractFormValues) {
    mutation.mutate(toInput(values), { onSuccess: () => onClose() });
  }

  const supplierOptions = (suppliers?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));
  const productOptions = (products?.items ?? []).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }));

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? 'Edit supplier contract' : 'New supplier contract'} centered size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save contract'}
            </Alert>
          )}
          <Select label="Supplier" data={supplierOptions} required searchable {...form.getInputProps('supplierId')} />
          <Select label="Product" data={productOptions} required searchable {...form.getInputProps('productId')} />
          <Select
            label="Pricing basis"
            data={PRICING_BASES.map((b) => ({ value: b, label: PRICING_BASIS_LABELS[b] }))}
            required
            {...form.getInputProps('pricingBasis')}
          />

          {form.values.pricingBasis !== 'MOISTURE_BAND' ? (
            <TextInput
              label="Price per tonne (ZAR)"
              placeholder="e.g. 850.00"
              required
              {...form.getInputProps('priceRand')}
            />
          ) : (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Moisture bands (contiguous, non-overlapping)
              </Text>
              <Text size="xs" c="dimmed">
                Each band covers a range of moisture content (MC%) and has its own price — wetter loads carry more water
                weight for the same dry mass, so higher-MC bands are normally priced lower. Bands must run back-to-back
                with no gaps or overlaps (0–20%, 20–35%, and so on).
              </Text>
              {form.values.bands.map((band, index) => (
                <Group key={index} align="flex-end" gap="xs">
                  <NumberInput
                    label="MC min %"
                    min={0}
                    max={100}
                    value={band.mcMin}
                    onChange={(v) => form.setFieldValue(`bands.${index}.mcMin`, Number(v) || 0)}
                    w={110}
                  />
                  <NumberInput
                    label="MC max %"
                    min={0}
                    max={100}
                    value={band.mcMax}
                    onChange={(v) => form.setFieldValue(`bands.${index}.mcMax`, Number(v) || 0)}
                    w={110}
                  />
                  <TextInput
                    label="Price per tonne (ZAR)"
                    value={band.priceRand}
                    onChange={(e) => form.setFieldValue(`bands.${index}.priceRand`, e.currentTarget.value)}
                    w={160}
                  />
                  <Button
                    color="red"
                    variant="subtle"
                    onClick={() => form.removeListItem('bands', index)}
                    aria-label="Remove band"
                  >
                    <IconTrash size={16} />
                  </Button>
                </Group>
              ))}
              {typeof form.errors.bands === 'string' && (
                <Text c="red" size="sm">
                  {form.errors.bands}
                </Text>
              )}
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => form.insertListItem('bands', { mcMin: 0, mcMax: 0, priceRand: '' })}
              >
                Add band
              </Button>
            </Stack>
          )}

          <Group grow>
            <TextInput label="Valid from" type="date" required {...form.getInputProps('validFrom')} />
            <TextInput label="Valid to (optional)" type="date" {...form.getInputProps('validTo')} />
          </Group>
          <Switch label="Active" {...form.getInputProps('isActive', { type: 'checkbox' })} />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button type="submit" disabled={!canOperate} loading={mutation.isPending}>
                  {editing ? 'Save changes' : 'Create contract'}
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function SupplierContractRegister() {
  const { data, isLoading, isError } = useSupplierContracts();
  const { data: suppliers } = useSuppliers();
  const { data: products } = useProducts();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<SupplierContractRow | null>(null);

  const supplierName = useMemo(() => new Map((suppliers?.items ?? []).map((s) => [s.id, s.name])), [suppliers]);
  const productName = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, `${p.sku} — ${p.name}`])), [products]);
  const rows = data?.items ?? [];
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26): on an empty
  // register the EmptyState below already offers "New contract" as the sole
  // call-to-action — showing the same button in the page header too rendered
  // TWO "New contract" buttons on a fresh tenant. Reuses the exact emptiness
  // condition the EmptyState branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: SupplierContractRow) {
    setEditing(row);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Supplier contracts"
        description="Set a standing price with a supplier — a flat rate, or a schedule that prices by moisture content."
        actions={
          showHeaderCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
              New contract
            </Button>
          ) : undefined
        }
      />

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load supplier contracts.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Supplier</Table.Th>
                <Table.Th>Product</Table.Th>
                <Table.Th>Basis</Table.Th>
                <Table.Th>Price / bands</Table.Th>
                <Table.Th>Valid from</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{supplierName.get(row.supplierId) ?? row.supplierId}</Table.Td>
                  <Table.Td>{productName.get(row.productId) ?? row.productId}</Table.Td>
                  <Table.Td>{PRICING_BASIS_LABELS[row.pricingBasis]}</Table.Td>
                  <Table.Td>
                    {row.pricingBasis === 'MOISTURE_BAND' ? (
                      <Text size="sm">{(row.bands ?? []).length} band(s)</Text>
                    ) : (
                      <Text size="sm">R{centsToRand(row.priceCentsPerTonne)} / t</Text>
                    )}
                  </Table.Td>
                  {/* SAST business date, not the browser's own timezone — a stored SAST
                      day-start instant renders one day early on a non-SAST host. */}
                  <Table.Td>{formatSastDate(row.validFrom)}</Table.Td>
                  <Table.Td>
                    <Badge color={row.isActive ? 'green' : 'gray'} variant="light">
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <EmptyState
                      icon={IconFileOff}
                      title="No supplier contracts yet"
                      hint="Set a standing price with a supplier so the Reorder report and receiving flow can price loads automatically."
                      action={
                        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
                          New contract
                        </Button>
                      }
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {/* Keyed by editing target so `useForm`'s `initialValues` — captured once
          at mount, per @mantine/form — are re-derived on every "Edit" click
          instead of showing the FIRST row ever edited. */}
      <ContractFormModal key={editing?.id ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editing={editing} />
    </Stack>
  );
}
